import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Avatar,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Empty,
  Badge,
} from "antd";
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { Container } from "./GroupManagerStyle";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
const { TextArea } = Input;

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
      about
      memberCount
      visibility
      joinPolicy
    }
  }
`;

const GROUP_MEMBERS = gql`
  query GroupMembers($groupId: String!) {
    groupMembers(groupId: $groupId) {
      user {
        _id
        email
        firstName
        lastName
        profilePictureUrl
      }
      joinedAt
    }
  }
`;

const CREATE_GROUP = gql`
  mutation CreateGroup($input: CreateGroupInput!) {
    createGroup(input: $input) {
      _id
    }
  }
`;

const UPDATE_GROUP = gql`
  mutation UpdateGroup($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      _id
    }
  }
`;

const ARCHIVE_GROUP = gql`
  mutation ArchiveGroup($groupId: String!) {
    archiveGroup(groupId: $groupId) {
      _id
    }
  }
`;

const ADD_GROUP_MEMBERS = gql`
  mutation AddGroupMembers($groupId: String!, $userIds: [String!]!) {
    addGroupMembers(groupId: $groupId, userIds: $userIds) {
      _id
    }
  }
`;

const REMOVE_GROUP_MEMBERS = gql`
  mutation RemoveGroupMembers($groupId: String!, $userIds: [String!]!) {
    removeGroupMembers(groupId: $groupId, userIds: $userIds) {
      _id
    }
  }
`;

export default function GroupManager({
  allSubscribers = [],
  highlightGroupId,
}) {
  const userInfo = useSelector((state) => state.userInfo.userInfo);

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isAddSubscriberModalOpen, setIsAddSubscriberModalOpen] =
    useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const selectGroupFromList = (groupList, preferredGroupId) => {
    if (preferredGroupId) {
      const targetGroup = groupList.find((group) => group._id === preferredGroupId);
      if (targetGroup) {
        return targetGroup;
      }
    }
    return groupList[0] || null;
  };

  // GraphQL Queries
  const [fetchGroups, { loading: loadingGroups, refetch: refetchGroups }] =
    useLazyQuery(GROUPS_BY_COACH, {
      variables: { coachId: userInfo.coach?._id, isArchived: false },
      onCompleted: (data) => {
        if (data?.groupsByCoach && data.groupsByCoach.length > 0) {
          setGroups(data.groupsByCoach);
          setSelectedGroup((currentGroup) => {
            if (
              highlightGroupId &&
              data.groupsByCoach.some((group) => group._id === highlightGroupId)
            ) {
              return selectGroupFromList(data.groupsByCoach, highlightGroupId);
            }
            return currentGroup || selectGroupFromList(data.groupsByCoach);
          });
        }
      },
      onError: (error) => {
        console.error("Error fetching groups:", error);
      },
      fetchPolicy: "network-only",
    });

  const [fetchGroupMembers, { loading: loadingMembers }] = useLazyQuery(
    GROUP_MEMBERS,
    {
      onCompleted: (data) => {
        if (data?.groupMembers) {
          const members = data.groupMembers.map((member) => ({
            _id: member.user._id,
            name: `${member.user.firstName || ""} ${member.user.lastName || ""
              }`.trim(),
            email: member.user.email,
            profile_image: member.user.profilePictureUrl,
            joinDate: member.joinedAt,
          }));
          setGroupMembers(members);
        }
      },
      onError: (error) => {
        console.error("Error fetching group members:", error);
      },
      fetchPolicy: "network-only",
    }
  );

  // GraphQL Mutations
  const [createGroup, { loading: creatingGroup }] = useMutation(CREATE_GROUP, {
    onCompleted: (data) => {
      message.success("Group created successfully");
      refetchGroups();
      setSelectedGroup(null);
      // 创建后选中新创建的 group
      if (data?.createGroup?._id) {
        setSelectedGroup(data.createGroup);
      }
    },
    onError: (error) => {
      console.error("Error creating group:", error);
    },
  });

  const [updateGroup, { loading: updatingGroup }] = useMutation(UPDATE_GROUP, {
    onCompleted: (data) => {
      message.success("Group updated successfully");
      setSelectedGroup(null);
      // 更新当前选中的 group 信息
      if (selectedGroup) {
        const updatedGroupData = {
          ...selectedGroup,
          name: editingGroup.name,
          about: editingGroup.about,
        };
        setSelectedGroup(updatedGroupData);
      }
      refetchGroups();
    },
    onError: (error) => {
      console.error("Error updating group:", error);
    },
  });

  const [addMembers, { loading: addingMembers }] = useMutation(
    ADD_GROUP_MEMBERS,
    {
      onCompleted: (data) => {
        message.success("Members added successfully");
        if (selectedGroup) {
          fetchGroupMembers({ variables: { groupId: selectedGroup._id } });
        }
        refetchGroups();
      },
      onError: (error) => {
        console.error("Error adding members:", error);
      },
    }
  );

  const [removeMembers, { loading: removingMembers }] = useMutation(
    REMOVE_GROUP_MEMBERS,
    {
      onCompleted: (data) => {
        message.success("Members removed successfully");
        if (selectedGroup) {
          fetchGroupMembers({ variables: { groupId: selectedGroup._id } });
        }
        setSelectedRowKeys([]);
        refetchGroups();
      },
      onError: (error) => {
        console.error("Error removing members:", error);
      },
    }
  );

  const [archiveGroup] = useMutation(ARCHIVE_GROUP, {
    onCompleted: (data) => {
      message.success("Group archived successfully");
      refetchGroups();
    },
    onError: (error) => {
      console.error("Error archiving group:", error);
    },
  });

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    } else {
      console.log("No coachId available");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  // Fetch group members when selected group changes
  useEffect(() => {
    if (selectedGroup?._id) {
      fetchGroupMembers({ variables: { groupId: selectedGroup._id } });
    } else {
      setGroupMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?._id]);

  useEffect(() => {
    if (!highlightGroupId || groups.length === 0) return;
    const targetGroup = groups.find((group) => group._id === highlightGroupId);
    if (targetGroup && selectedGroup?._id !== targetGroup._id) {
      setSelectedGroup(targetGroup);
    }
  }, [highlightGroupId, groups, selectedGroup?._id]);

  // Convert real subscribers to proper format
  const subscribers =
    allSubscribers.length > 0
      ? allSubscribers.map((sub) => ({
        _id: sub.user?._id,
        name: `${sub.user?.name || ""}`.trim() || "Unknown",
        email: sub.user?.email || "",
        profile_image: sub.user?.profile_image,
        joinDate: sub.joinDate,
      }))
      : [];

  // Get subscribers for selected group (from fetched group members)
  const groupSubscribers = groupMembers;

  // Get available subscribers (not in current group)
  const availableSubscribers = selectedGroup
    ? subscribers.filter(
      (sub) => !groupMembers.some((member) => member._id === sub._id)
    )
    : subscribers;

  // Handle create/edit group
  const handleGroupSubmit = async (values) => {
    try {
      if (editingGroup) {
        // Update existing group
        await updateGroup({
          variables: {
            input: {
              groupId: editingGroup._id,
              name: values.name,
              about: values.about,
            },
          },
        });
      } else {
        // Create new group
        await createGroup({
          variables: {
            input: {
              name: values.name,
              about: values.about,
              visibility: "PRIVATE",
              joinPolicy: "REQUEST_TO_JOIN",
            },
          },
        });
      }
      fetchGroups();
      setIsGroupModalOpen(false);
      setEditingGroup(null);
      form.resetFields();
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  // Handle archive group
  const handleArchiveGroup = async (groupId) => {
    try {
      await archiveGroup({ variables: { groupId } });
      fetchGroups();
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  // Handle add subscribers to group
  const handleAddSubscribers = async (subscriberIds) => {
    if (!selectedGroup) return;

    try {
      await addMembers({
        variables: {
          groupId: selectedGroup._id,
          userIds: subscriberIds,
        },
      });
      setIsAddSubscriberModalOpen(false);
      form.resetFields();
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  // Handle remove subscribers from group
  const handleRemoveSubscribers = async () => {
    if (!selectedGroup || selectedRowKeys.length === 0) return;

    try {
      await removeMembers({
        variables: {
          groupId: selectedGroup._id,
          userIds: selectedRowKeys,
        },
      });
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  // Group list columns
  const groupColumns = [
    {
      title: "Group Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div
            style={{ fontWeight: 600, fontSize: "15px", marginBottom: "4px" }}
          >
            {name}
          </div>
        </div>
      ),
    },
    {
      title: "Subscribers",
      dataIndex: "memberCount",
      key: "subscribers",
      width: 60,
      align: "center",
      render: (count) => (
        <Badge
          count={count || 0}
          showZero
          style={{ backgroundColor: "#1890ff" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setEditingGroup(record);
              form.setFieldsValue(record);
              setIsGroupModalOpen(true);
            }}
          />
          <Popconfirm
            title="Archive group"
            description="Are you sure you want to archive this group?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleArchiveGroup(record._id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Subscriber columns
  const subscriberColumns = [
    {
      title: "Subscriber",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar
            src={record.profile_image}
            icon={!record.profile_image ? <UserOutlined /> : null}
            style={{
              backgroundColor: !record.profile_image ? "#1890ff" : undefined,
            }}
          />
          <div>
            <div style={{ fontWeight: 500, fontSize: "14px" }}>{name}</div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Joined Date",
      dataIndex: "joinDate",
      key: "joinDate",
      width: 150,
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <Container>
      <div className="content-layout">
        {/* Left side - Group list */}
        <div className="group-list-section">
          <Card
            title="All Groups"
            className="group-list-card"
            extra={
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingGroup(null);
                    form.resetFields();
                    setIsGroupModalOpen(true);
                  }}
                >
                  Create Group
                </Button>
              </Space>
            }
          >
            <Table
              columns={groupColumns}
              dataSource={groups}
              rowKey="_id"
              pagination={false}
              loading={loadingGroups}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedGroup(record);
                  setSelectedRowKeys([]);
                },
                className:
                  selectedGroup?._id === record._id ? "selected-row" : "",
                style: {
                  cursor: "pointer",
                },
              })}
            />
          </Card>
        </div>

        {/* Right side - Group detail */}
        <div className="group-detail-section">
          {selectedGroup ? (
            <Card
              title={
                <div>
                  <div style={{ fontSize: "18px", fontWeight: 600 }}>
                    {selectedGroup.name}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#8c8c8c",
                      fontWeight: 400,
                      marginTop: "4px",
                    }}
                  >
                    {selectedGroup.about}
                  </div>
                </div>
              }
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<UserAddOutlined />}
                    onClick={() => setIsAddSubscriberModalOpen(true)}
                  >
                    Add Subscribers
                  </Button>
                  {selectedRowKeys.length > 0 && (
                    <Popconfirm
                      title="Remove subscribers"
                      description={`Remove ${selectedRowKeys.length} subscriber(s) from this group?`}
                      onConfirm={handleRemoveSubscribers}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        danger
                        size="small"
                        icon={<UserDeleteOutlined />}
                        loading={removingMembers}
                      >
                        Remove ({selectedRowKeys.length})
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              }
              className="group-detail-card"
            >
              <Table
                columns={subscriberColumns}
                dataSource={groupSubscribers}
                rowKey="_id"
                rowSelection={rowSelection}
                pagination={false}
                loading={loadingMembers}
                scroll={{ y: "calc(100vh - 520px)" }}
                locale={{
                  emptyText: (
                    <Empty description="No subscribers in this group yet">
                      <Button
                        type="primary"
                        onClick={() => setIsAddSubscriberModalOpen(true)}
                      >
                        Add Subscribers
                      </Button>
                    </Empty>
                  ),
                }}
              />
            </Card>
          ) : (
            <Card className="group-detail-card">
              <Empty
                description="Select a group to view details"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )}
        </div>
      </div>

      {/* Create/Edit Group Modal */}
      <Modal
        title={editingGroup ? "Edit Group" : "Create New Group"}
        open={isGroupModalOpen}
        onCancel={() => {
          setIsGroupModalOpen(false);
          setEditingGroup(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={creatingGroup || updatingGroup}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleGroupSubmit}>
          <Form.Item
            label="Group Name"
            name="name"
            rules={[
              { required: true, message: "Please enter group name" },
              { max: 50, message: "Group name cannot exceed 50 characters" },
            ]}
          >
            <Input placeholder="Enter group name" size="large" />
          </Form.Item>

          <Form.Item
            label="About"
            name="about"
            rules={[
              { max: 200, message: "Description cannot exceed 200 characters" },
            ]}
          >
            <TextArea
              placeholder="Describe this group"
              rows={4}
              showCount
              maxLength={200}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Subscribers Modal */}
      <Modal
        title="Add Subscribers to Group"
        open={isAddSubscriberModalOpen}
        onCancel={() => setIsAddSubscriberModalOpen(false)}
        onOk={() => {
          const selectedIds = form.getFieldValue("subscribers") || [];
          if (selectedIds.length === 0) {
            message.warning("Please select at least one subscriber");
            return;
          }
          handleAddSubscribers(selectedIds);
          form.resetFields();
        }}
        confirmLoading={addingMembers}
        width={700}
      >
        <Form form={form}>
          <Form.Item
            name="subscribers"
            label="Select Subscribers"
            rules={[
              {
                required: true,
                message: "Please select at least one subscriber",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select subscribers to add"
              size="large"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={availableSubscribers.map((sub) => ({
                label: `${sub.name} (${sub.email})`,
                value: sub._id,
              }))}
            />
          </Form.Item>
        </Form>
        <div style={{ color: "#8c8c8c", fontSize: "13px", marginTop: "8px" }}>
          {availableSubscribers.length} subscriber(s) available to add
        </div>
      </Modal>
    </Container>
  );
}
